import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { CircleAlert, Copy, Download, ExternalLink, History, Link2, Lock, RotateCcw, TriangleAlert } from "lucide-react";
import { Badge, Button, Card, CardDescription, CardHeader, CardTitle, ConfirmDialog, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Field, Input, NativeSelect, toast } from "@dishfolio/ui";
import { api, ApiError, withOrg } from "../lib/api.ts";
import { useOrg } from "../lib/org.tsx";
import { dateTime } from "../lib/format.ts";
import { useMenu, useVersions } from "../lib/queries.ts";
import { PageContainer } from "../components/Page.tsx";
import { notifyError, UpgradeCard } from "../components/Upgrade.tsx";
import { Spinner } from "../components/Spinner.tsx";

type Issue = { blockId: string | null; path: string; message: string; severity: "error" | "warning" };
type ReviewItem = { id: string; name: string; reasons: string[] };

export function PublishDialog({ menuId, open, onOpenChange }: { menuId: string; open: boolean; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient();
  const check = useQuery({
    queryKey: ["publish-check", menuId],
    queryFn: () => api.get<{ ok: boolean; issues: Issue[]; reviewItems: ReviewItem[]; draftRevision: number }>(`/menus/${menuId}/publish-check`),
    enabled: open,
    staleTime: 0,
  });
  const [note, setNote] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [result, setResult] = useState<{ publicUrl: string } | null>(null);
  const errors = check.data?.issues.filter((i) => i.severity === "error") ?? [];
  const warnings = check.data?.issues.filter((i) => i.severity === "warning") ?? [];
  const publish = async () => {
    setPublishing(true);
    try {
      const res = await api.post<{ publicUrl: string; versionId: string }>(`/menus/${menuId}/publish`, { expectedRevision: check.data?.draftRevision, note: note || undefined });
      setResult(res);
      await Promise.all([qc.invalidateQueries({ queryKey: ["menu", menuId] }), qc.invalidateQueries({ queryKey: ["versions", menuId] }), qc.invalidateQueries({ queryKey: ["menus"] }), qc.invalidateQueries({ queryKey: ["org"] })]);
    } catch (err) {
      if (err instanceof ApiError && (err.code === "review_required" || err.code === "conflict")) await check.refetch();
      notifyError(err);
    } finally {
      setPublishing(false);
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) setResult(null);
      }}
    >
      <DialogContent className="sm:max-w-xl">
        {result ? (
          <>
            <DialogHeader>
              <DialogTitle>Your menu is live 🎉</DialogTitle>
              <DialogDescription>Guests scanning your QR code now see this version. Earlier versions stay available for rollback.</DialogDescription>
            </DialogHeader>
            <div className="flex items-center gap-2 rounded-md border bg-muted/50 p-2 text-sm">
              <span className="flex-1 truncate font-mono">{result.publicUrl}</span>
              <Button size="sm" variant="ghost" onClick={() => (navigator.clipboard.writeText(result.publicUrl), toast.success("Link copied"))}>
                <Copy />
              </Button>
            </div>
            <DialogFooter>
              <Button asChild variant="outline">
                <a href={result.publicUrl} target="_blank" rel="noreferrer">
                  <ExternalLink /> Open menu
                </a>
              </Button>
              <Button asChild>
                <Link to="/menus/$menuId/publish" params={{ menuId }} onClick={() => onOpenChange(false)}>
                  Get QR code
                </Link>
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Publish menu</DialogTitle>
              <DialogDescription>Publishing creates an immutable version. The live menu switches over in one step — guests never see a half-finished menu.</DialogDescription>
            </DialogHeader>
            {check.isPending ? (
              <div className="flex justify-center py-6">
                <Spinner />
              </div>
            ) : (
              <div className="grid gap-3">
                {check.data?.reviewItems.length ? (
                  <div className="rounded-lg border border-warning/50 bg-warning/10 p-3 text-sm">
                    <div className="flex items-center gap-1.5 font-medium">
                      <CircleAlert className="size-4" /> {check.data.reviewItems.length} items need review first
                    </div>
                    <ul className="mt-2 grid max-h-48 gap-1 overflow-y-auto">
                      {check.data.reviewItems.map((r) => (
                        <li key={r.id}>
                          <span className="font-medium">{r.name}</span> <span className="text-muted-foreground">— {r.reasons.join("; ")}</span>
                        </li>
                      ))}
                    </ul>
                    <Button asChild size="sm" variant="outline" className="mt-2">
                      <Link to="/menus/$menuId" params={{ menuId }} onClick={() => onOpenChange(false)}>
                        Review items
                      </Link>
                    </Button>
                  </div>
                ) : null}
                {errors.length ? (
                  <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm">
                    <div className="flex items-center gap-1.5 font-medium text-destructive">
                      <TriangleAlert className="size-4" /> Fix these before publishing
                    </div>
                    <ul className="mt-1 list-disc pl-5">
                      {errors.map((e, i) => (
                        <li key={i}>{e.message}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {warnings.length ? (
                  <div className="rounded-lg border p-3 text-sm">
                    <div className="font-medium">Heads up</div>
                    <ul className="mt-1 list-disc pl-5 text-muted-foreground">
                      {warnings.map((e, i) => (
                        <li key={i}>{e.message}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {check.data?.ok ? <p className="text-sm text-success">Everything looks good.</p> : null}
                <Field label="Version note (optional)">
                  <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Autumn specials" maxLength={200} />
                </Field>
              </div>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={publish} loading={publishing} disabled={!check.data?.ok}>
                Publish now
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function QrCard({ menuId }: { menuId: string }) {
  const { org } = useOrg();
  const [fg, setFg] = useState("#111111");
  const [bg, setBg] = useState("#ffffff");
  const [size, setSize] = useState(1024);
  const [source, setSource] = useState("");
  const custom = org?.plan.qr !== "standard";
  const params = new URLSearchParams({ size: String(size), ecc: "M" });
  if (custom) {
    params.set("fg", fg.replace("#", ""));
    params.set("bg", bg.replace("#", ""));
  }
  if (source && org?.plan.analytics !== "basic") params.set("source", source);
  const preview = withOrg(`/api/v1/menus/${menuId}/qr?${params.toString()}&format=svg`);
  return (
    <Card>
      <CardHeader>
        <CardTitle>QR code</CardTitle>
        <CardDescription>Print it on tables, windows and flyers. The code always opens the latest published version.</CardDescription>
      </CardHeader>
      <div className="grid gap-5 sm:grid-cols-[200px_1fr]">
        <div className="flex aspect-square items-center justify-center rounded-xl border p-3" style={{ background: custom ? bg : "#fff" }}>
          <img src={preview} alt="Menu QR code" className="size-full" />
        </div>
        <div className="grid content-start gap-3">
          {custom ? (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Foreground">
                <Input type="color" value={fg} onChange={(e) => setFg(e.target.value)} className="h-9 p-1" />
              </Field>
              <Field label="Background">
                <Input type="color" value={bg} onChange={(e) => setBg(e.target.value)} className="h-9 p-1" />
              </Field>
            </div>
          ) : (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Lock className="size-3" /> Custom QR colors are available on Pro.
            </p>
          )}
          <Field label="Campaign tag" hint={org?.plan.analytics === "basic" ? "Track which QR placement guests used (Pro)." : "Shown in analytics as the visit source, e.g. window, table-tent, flyer."}>
            <Input value={source} disabled={org?.plan.analytics === "basic"} onChange={(e) => setSource(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))} placeholder="table-tent" />
          </Field>
          <Field label="PNG size">
            <NativeSelect value={size} onChange={(e) => setSize(Number(e.target.value))}>
              <option value={512}>512 px (screens)</option>
              <option value={1024}>1024 px (print)</option>
              <option value={2048}>2048 px (large print)</option>
            </NativeSelect>
          </Field>
          <div className="flex flex-wrap gap-2">
            <Button asChild>
              <a href={withOrg(`/api/v1/menus/${menuId}/qr?${params.toString()}&format=png&download=1`)}>
                <Download /> PNG
              </a>
            </Button>
            <Button asChild variant="outline">
              <a href={withOrg(`/api/v1/menus/${menuId}/qr?${params.toString()}&format=svg&download=1`)}>
                <Download /> SVG
              </a>
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

export function MenuPublishPage() {
  const { menuId } = useParams({ from: "/app/menus/$menuId/publish" });
  const menu = useMenu(menuId);
  const versions = useVersions(menuId);
  const qc = useQueryClient();
  const { can, org } = useOrg();
  const [rollback, setRollback] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [unpublish, setUnpublish] = useState(false);
  const m = menu.data?.menu;
  const makePreview = async () => {
    const res = await api.post<{ url: string }>(`/menus/${menuId}/preview-link`);
    setPreview(res.url);
    await navigator.clipboard?.writeText(res.url).catch(() => undefined);
    toast.success("Preview link copied — valid for 7 days");
  };
  return (
    <PageContainer wide>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="grid content-start gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Public address</CardTitle>
              <CardDescription>{m?.status === "published" ? "Your menu is live here." : "Publish the menu to make this address work."}</CardDescription>
            </CardHeader>
            {m ? (
              <div className="flex items-center gap-2 rounded-md border bg-muted/50 p-2 text-sm">
                <span className="flex-1 truncate font-mono">{m.publicUrl}</span>
                <Button size="sm" variant="ghost" onClick={() => (navigator.clipboard.writeText(m.publicUrl), toast.success("Link copied"))} aria-label="Copy link">
                  <Copy />
                </Button>
                <Button size="sm" variant="ghost" asChild>
                  <a href={m.publicUrl} target="_blank" rel="noreferrer" aria-label="Open">
                    <ExternalLink />
                  </a>
                </Button>
              </div>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={makePreview}>
                <Link2 /> Share draft preview
              </Button>
              {m?.status === "published" && can("menu:publish") ? (
                <Button variant="ghost" size="sm" onClick={() => setUnpublish(true)}>
                  Unpublish
                </Button>
              ) : null}
            </div>
            {preview ? <p className="truncate text-xs text-muted-foreground">Preview: {preview}</p> : null}
          </Card>
          {m?.status === "published" ? <QrCard menuId={menuId} /> : null}
          {org?.plan.qr !== "table" ? (
            <UpgradeCard plan="Business" title="Table QR codes & ordering" description="Give every table its own QR code so guests can order from their phone and staff get orders instantly." />
          ) : (
            <Card>
              <CardTitle>Table QR codes</CardTitle>
              <p className="text-sm text-muted-foreground">Table-specific codes identify the table for ordering.</p>
              <Button asChild size="sm" variant="outline" className="justify-self-start">
                <Link to="/restaurants/$restaurantId/tables" params={{ restaurantId: m?.restaurantId ?? "" }}>
                  Manage tables
                </Link>
              </Button>
            </Card>
          )}
        </div>
        <Card className="content-start">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="size-4" /> Published versions
            </CardTitle>
            <CardDescription>Roll back instantly if something went wrong. Rolling back also restores that design into your draft.</CardDescription>
          </CardHeader>
          {versions.data?.length ? (
            <ul className="grid gap-2">
              {versions.data.map((v) => (
                <li key={v.id} className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      {dateTime(v.publishedAt)} {v.isCurrent ? <Badge variant="success">Live</Badge> : null}
                    </div>
                    {v.note ? <div className="truncate text-xs text-muted-foreground">{v.note}</div> : null}
                  </div>
                  {!v.isCurrent && can("menu:publish") ? (
                    <Button size="sm" variant="outline" onClick={() => setRollback(v.id)}>
                      <RotateCcw /> Roll back
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">No published versions yet.</p>
          )}
        </Card>
      </div>
      <ConfirmDialog
        open={Boolean(rollback)}
        onOpenChange={(o) => !o && setRollback(null)}
        title="Roll back to this version?"
        description="Guests immediately see that version — its design and its dishes and prices. Its design is also restored into your draft; dish edits you made since then stay in your draft."
        confirmLabel="Roll back"
        onConfirm={async () => {
          try {
            await api.post(`/menus/${menuId}/rollback`, { versionId: rollback });
            await Promise.all([qc.invalidateQueries({ queryKey: ["versions", menuId] }), qc.invalidateQueries({ queryKey: ["draft", menuId] }), qc.invalidateQueries({ queryKey: ["menu", menuId] })]);
            toast.success("Rolled back");
          } catch (err) {
            notifyError(err);
          }
        }}
      />
      <ConfirmDialog
        open={unpublish}
        onOpenChange={setUnpublish}
        title="Unpublish this menu?"
        description="Guests scanning your QR code will see “menu not available” until you publish again."
        destructive
        confirmLabel="Unpublish"
        onConfirm={async () => {
          await api.post(`/menus/${menuId}/unpublish`);
          await qc.invalidateQueries({ queryKey: ["menu", menuId] });
        }}
      />
    </PageContainer>
  );
}
